import type {} from '@mui/lab/themeAugmentation';
import { createTheme, ThemeOptions, toggleButtonClasses } from '@mui/material';
import { grey } from '@mui/material/colors';

import { palette } from './palette';

// TODO Убрать все лишнее и актуализировать
export const createMuiTheme = (options?: ThemeOptions, isWebView = false) =>
  createTheme({
    breakpoints: {
      values: {
        xs: 0, // фиксируется на 375 и начинается масштабирование
        sm: 576,
        md: 768,
        lg: 992,
        xl: 1200 + 48,
        xxl: 1440 + 48,
      },
    },
    // @ts-ignore
    palette: palette,
    typography: {
      fontFamily: 'Inter, sans-serif',
      // h1: {},
      // h2: {},
      // h3: {},
      h4: {
        letterSpacing: '0.25px',
      },
      // h5: {},
      h6: {
        letterSpacing: '0.15px',
      },
      body1: {
        letterSpacing: '0.15px',
      },
      body2: {
        letterSpacing: '0.17px',
      },
      subtitle1: {
        letterSpacing: '0.15px',
      },
      subtitle2: {
        letterSpacing: '0.1px',
      },
      // custom
      link: {
        fontSize: 14,
        lineHeight: 'normal',
        fontWeight: 600,
      },
      // Headline/Menu 16
      subtitle3: {
        fontSize: 16,
        fontWeight: 500,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            userSelect: isWebView ? 'none' : 'unset',
          },
          body: {
            minWidth: isWebView ? 'unset' : 375,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            background: isWebView ? '#FFFFFF' : '#F5F5F5',
            overflowX: 'hidden',
            overflowY: 'auto',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              WebkitAppearance: 'none',
              width: '6px',
              height: '10px',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              background: 'rgba(48, 38, 85, 0.1) !important',
              cursor: 'pointer',
              borderRadius: '5px',
              WebkitTransition: 'color 0.2s ease',
              transition: 'color 0.2s ease',
            },
            '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
              background: '#faf9ff !important',
              borderRadius: '0',
            },
          },
          main: {
            flex: '1 0 auto',
          },
          '#root': {
            position: 'relative',
            minHeight: isWebView ? 'unset' : '100vh',
            display: 'flex',
            flexDirection: 'column',
          },
          img: {
            height: 'auto',
            maxWidth: '100%',
          },
          '.router-link': {
            textDecoration: 'none',
          },
          a: {
            '&:visited': {
              color: 'black',
            },
            '&:hover': {
              color: '#895070',
            },
          },
          input: {
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: '0',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          size: 'medium',
          disableElevation: true,
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            minWidth: 48,
            textTransform: 'none',
          },
          sizeLarge: {
            letterSpacing: '0.46px',
          },
          sizeMedium: {
            letterSpacing: '0.4px',
          },
          sizeSmall: {
            letterSpacing: '0.46px',
          },
          text: {
            transition: 'all .3s ease',
            '&:hover': {
              color: '',
            },
          },
        },
        variants: [
          {
            props: { variant: 'outlined', color: 'primary' },
            style: ({ theme }) => ({
              color: theme.palette.primary.dark,
              borderColor: '#25553580',
              '&:hover': {
                borderColor: '#25553580',
                backgroundColor: '#0090300A',
              },
            }),
          },
          {
            props: { variant: 'text', color: 'primary' },
            style: ({ theme }) => ({
              color: theme.palette.primary.dark,
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }),
          },
          {
            props: { variant: 'contained2' },
            style: ({ theme }) => ({
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.grey[100],
              '&:hover': {
                backgroundColor: theme.palette.grey[200],
              },
            }),
          },
          {
            props: { variant: 'containedPrimary2' },
            style: ({ theme }) => ({
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },
              [theme.breakpoints.down('lg')]: {
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              },
            }),
          },
        ],
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            '.MuiButtonGroup-grouped:not(:last-of-type)': {
              borderColor: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            letterSpacing: '0.15px',
            color: theme.palette.text.primary,
            '.MuiFormLabel-asterisk': {
              color: `${theme.palette.error.main}!important`,
            },
            [theme.breakpoints.down('sm')]: {
              fontSize: '0.875rem',
            },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            border: 'none',
            backgroundColor: grey[200],
            boxShadow:
              // eslint-disable-next-line max-len
              '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20);',
            [`& .${toggleButtonClasses.root}:last-child`]: {
              borderRight: 'none',
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            textTransform: 'none',
            borderTop: 'none',
            borderBottom: 'none',
            borderLeft: 'none',
            borderColor: theme.palette.primary.main,
            [theme.breakpoints.down('sm')]: {
              borderRight: 'none',
            },
            [`&.${toggleButtonClasses.selected}`]: {
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            zIndex: 9001,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          backdrop: {
            background: 'rgba(71, 71, 71, 0.7)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            background: 'transparent',
          },
          paper: ({ theme }) => ({
            borderRadius: theme.spacing(1),
          }),
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
        },
        styleOverrides: {
          tooltip: ({ theme }) => ({
            color: 'black',
            padding: 16,
            fontSize: 14,
            fontWeight: 400,
            borderRadius: 10,
            backgroundColor: 'white',
            boxShadow:
              // eslint-disable-next-line max-len
              '0px 5px 26px 4px rgba(0, 0, 0, 0.12), 0px 14px 21px 2px rgba(0, 0, 0, 0.14), 0px 7px 9px -4px rgba(0, 0, 0, 0.20)',
            [theme.breakpoints.up('md')]: {
              maxWidth: 450,
            },
          }),
          arrow: {
            '&::before': {
              backgroundColor: 'white',
            },
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            backgroundColor: 'white',
          },
          sizeSmall: ({ theme }) => ({
            [theme.breakpoints.down('sm')]: {
              minHeight: 40,
            },
          }),
          inputSizeSmall: ({ theme }) => ({
            [theme.breakpoints.down('sm')]: {
              // Шрифт инпута ниже сделали 14px/0.875rem а рамках общего уменьшение размеров
              // + подгоняли зависимые компоненты инпута под 14px
              // fontSize: '0.875rem',
              // Но iOS увеличивает масштаб приложения при фокусе на инпут, выглядит очень плохо
              // Вернуться к 16px будет стоит дорого, поэтому пока сделал такой костыль
              // Визуально уменьшаем через transform и подгоняем другими свойствами к варианту с 14px без изменения шрифта
              // Таким образом обманываем iOS, взяли на подумать про не плохо ли в целом уменьшение интерфейса
              width: '114.29%', // 16/14 * 100 = 114.29%
              transform: 'scale(0.875)', // 14/16 * 100 = 87.5%
              transformOrigin: 'left top',
              marginBottom: '-5px', // подогнал визуально
              marginRight: '-14.29%', // подогнал визуально
            },
          }),
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          notched: false,
        },
      },
      MuiMenu: {
        styleOverrides: {
          root: {
            background: 'unset',
          },
          list: {
            padding: 0,
          },
        },
      },
      MuiTimeline: {
        styleOverrides: {
          root: {
            padding: `6px 0`,
          },
        },
      },
      MuiTimelineOppositeContent: {
        styleOverrides: {
          root: {
            padding: 6,
          },
        },
      },
      MuiTimelineContent: {
        styleOverrides: {
          root: {
            padding: 6,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44,
          },
        },
      },
      MuiTab: {
        defaultProps: {
          iconPosition: 'start',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: 15,
            lineHeight: '26px',
            fontWeight: 500,
            minHeight: 'inherit',
            color: theme.palette.text.secondary,
            textTransform: 'none',
            transition: theme.transitions.create('color'),
            '&:active': {
              color: theme.palette.primary.main,
            },
            '&:hover': {
              color: theme.palette.primary.dark,
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          labelMedium: ({ theme }) => ({
            marginTop: theme.spacing(0.2),
          }),
          filledSecondary: ({ theme }) => ({
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.light,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
          }),
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiStack: {
        defaultProps: {
          useFlexGap: true,
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            transform: 'none',
          },
        },
      },
      MuiMobileStepper: {
        styleOverrides: {
          dot: {
            margin: '0 4px',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.custom.icon.primary,
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            whiteSpace: 'pre-wrap',
          },
        },
      },
    },
    ...(options ?? {}),
  });
